import {afterNextRender, Component, inject, Injector, input, OnInit, signal, WritableSignal} from '@angular/core';
import {SidebarService} from "./sidebar/sidebar.service";
import {NgbToast} from "@ng-bootstrap/ng-bootstrap/toast";
import {NavbarComponent} from '../navbar/navbar.component';
import {NgOptimizedImage} from '@angular/common';
import {ToastContainerComponent} from '../toast/toast-container/toast-container.component';
import {AboutMeSectionComponent} from './about-me-section/about-me-section.component';
import {FirstVisitSectionComponent} from './first-visit-section/first-visit-section.component';
import {ServicesSectionComponent} from './services-section/services-section.component';
import {GallerySectionComponent} from './gallery-section/gallery-section.component';
import {ReviewsSectionComponent} from './reviews-section/reviews-section.component';
import {FaqSectionComponent} from './faq-section/faq-section.component';
import {EmailMessageSectionComponent} from './email-message-section/email-message-section.component';
import {ContactSectionComponent} from './contact-section/contact-section.component';
import {FooterComponent} from './footer/footer.component';
import {SeoService} from "../seo/seo.service";
import {HomepageNoticeFetchService} from "./homepage-notice-fetch-service";
import {HomepageNotice} from "./homepage-notice";
import {EmailMessageSectionWrapperComponent} from "./email-message-section/email-message-section-wrapper.component";
import {
  EmailMessageSectionPlaceholderComponent
} from "./email-message-section/email-message-section-placeholder.component";
import {FirestoreStructuredData} from "../seo/structured-data/firestore-structured-data";
import {SchedulingService} from "../scheduling.service";
import {TrackingService} from "../tracking/tracking.service";
import {scheduleInitiated} from "../tracking/tracking-event";

const FOOTER_THRESHOLD = 50;

interface FooterState {
  onScroll(): void;
}

class Visible implements FooterState {
  private readonly footer: HTMLElement;
  private readonly homePageComponent: HomePageComponent;

  constructor(footer: HTMLElement, homePageComponent: HomePageComponent) {
    this.footer = footer;
    this.homePageComponent = homePageComponent;
  }

  onScroll() {
    if (window.scrollY > FOOTER_THRESHOLD) {
      this.footer.classList.add('opacity-0');
      this.homePageComponent.changeState(new Hidden(this.footer, this.homePageComponent));
    }
  }
}

class Hidden implements FooterState {
  private readonly footer: HTMLElement;
  private readonly homePageComponent: HomePageComponent;

  constructor(footer: HTMLElement, homePageComponent: HomePageComponent) {
    this.footer = footer;
    this.homePageComponent = homePageComponent;
  }

  onScroll() {
    if (window.scrollY <= FOOTER_THRESHOLD) {
      this.footer.classList.remove('opacity-0');
      this.homePageComponent.changeState(new Visible(this.footer, this.homePageComponent));
    }
  }
}

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrl: './home-page.component.scss',
  imports: [NavbarComponent, NgbToast, NgOptimizedImage, ToastContainerComponent, AboutMeSectionComponent, FirstVisitSectionComponent, ServicesSectionComponent, GallerySectionComponent, ReviewsSectionComponent, FaqSectionComponent, EmailMessageSectionComponent, ContactSectionComponent, FooterComponent, EmailMessageSectionWrapperComponent, EmailMessageSectionPlaceholderComponent]
})
export class HomePageComponent implements OnInit {

  protected readonly firestoreStructuredData = input.required<FirestoreStructuredData>();
  protected homepageNotice: WritableSignal<HomepageNotice | null> = signal({
    active: false,
    title: '',
    details: ''
  });

  private footerState: FooterState | undefined;
  private revealableElements: NodeListOf<Element> | undefined;

  constructor(private readonly sidebarService: SidebarService,
              private readonly trackingService: TrackingService,
              private readonly seoService: SeoService,
              private readonly injector: Injector) {
    const homepageNoticeService = inject(HomepageNoticeFetchService);
    inject(SchedulingService).scheduleLowPriorityTask(() => {
      homepageNoticeService.fetchHomepageNotice()
        .then(value => {
          if (value) {
            this.homepageNotice.set(value);
          }
        })
        .catch(error => console.log(error));
    });

    afterNextRender(() => {
      this.revealableElements = document.querySelectorAll(".reveal");
      window.addEventListener("scroll", this.reveal.bind(this));
      window.addEventListener("scroll", () => this.sidebarService.closeSidebar());
      const footer = document.getElementById('footer') as HTMLElement;
      this.footerState = new Visible(footer, this);
      window.addEventListener("scroll", () => this.footerState!.onScroll());
    });
  }

  ngOnInit() {
    this.seoService.setTagsForHomepage(this.firestoreStructuredData());
  }

  changeState(newState: FooterState) {
    this.footerState = newState;
  }

  async openBooksyDialog() {
    this.trackingService.track(scheduleInitiated());
    const {NgbModal} = await import("@ng-bootstrap/ng-bootstrap/modal");
    const {BooksyDialogComponent} = await import("./booksy-dialog/booksy-dialog.component");
    this.injector.get(NgbModal).open(BooksyDialogComponent, {
      keyboard: false,
      beforeDismiss: () => false
    });
  }

  reveal() {
    this.revealableElements!.forEach(e => {
      const elementTop = e.getBoundingClientRect().top;
      if (elementTop < window.innerHeight - 50) {
        e.classList.add("active");
      } else {
        e.classList.remove("active");
      }
    });
  }

}
